import { alpha, type Theme } from "@mui/material";
import type { AnalysisCardId } from "src-next/modules/clinical-insights/types/analysisTypes";

export const SECTION_GAP = 4;
export const PANEL_PADDING = 3.5;
export const COLUMN_GAP = 5;
/** Espaço entre os cartões e a linha central de redimensionar (desktop). */
export const WORKSPACE_SPLIT_GUTTER = 2;
export const TABLE_ROW_PY = 1.25;
export const LEFT_BLOCK_GAP = 4;
export const RESULT_SURFACE_BORDER = 3;

export const BG_ALPHA = 0.06;
export const BORDER_ALPHA = 0.3;
export const DIMMED_ALPHA = 0.3;
export const FRAME_BG_ALPHA = 0.04;
export const SELECTED_CARD_BG_ALPHA = 0.07;
export const SELECTED_ROW_BG_ALPHA = 0.06;
export const HIGHLIGHT_FLASH_ALPHA = 0.12;

/** Tipografia mais legível (público 50+). */
export const READABLE_TITLE_SX = { fontSize: "1.125rem", lineHeight: 1.35 } as const;
export const READABLE_BODY_SX = { fontSize: "1.0625rem", lineHeight: 1.45 } as const;
export const READABLE_SECONDARY_SX = { fontSize: "1rem", lineHeight: 1.45 } as const;

/** Ranking: janela com N linhas visíveis e scroll interno. */
export const RANKING_SCROLL_VISIBLE_ROWS = 3;
export const RANKING_ROW_HEIGHT = 58;
export const RANKING_ROW_GAP_PX = 10;

export function rankingScrollViewportHeight(
  visibleRows = RANKING_SCROLL_VISIBLE_ROWS
): number {
  return visibleRows * RANKING_ROW_HEIGHT + (visibleRows - 1) * RANKING_ROW_GAP_PX;
}

/** Scroll interno dos painéis do workspace (thumb na cor da análise, afastado do conteúdo). */
/** Container queries: cada cartão esquerdo/direito reage à própria largura. */
export const INSIGHT_PANEL_CONTAINER_SX = {
  containerType: "inline-size",
  containerName: "insight-panel",
} as const;

export const INSIGHT_PANEL_NARROW = "@container insight-panel (max-width: 440px)";
export const INSIGHT_PANEL_COMPACT = "@container insight-panel (max-width: 360px)";
export const INSIGHT_PANEL_WIDE = "@container insight-panel (min-width: 441px)";

/** Chip de situação — corpo legível (50+), rótulo sempre em uma linha. */
export function insightSituationChipSx(accentColor: string, filled = false) {
  return {
    display: "inline-block",
    width: "max-content",
    maxWidth: "100%",
    ...READABLE_BODY_SX,
    fontWeight: 600,
    px: 1.35,
    py: 0.45,
    borderRadius: 10,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    border: "1px solid",
    borderColor: filled ? accentColor : alpha(accentColor, 0.28),
    bgcolor: filled ? accentColor : alpha(accentColor, 0.06),
    color: filled ? "#fff" : accentColor,
    [INSIGHT_PANEL_COMPACT]: { px: 1.15, py: 0.4 },
  } as const;
}

export const WORKSPACE_MIN_PANEL_PX = 272;

export function insightPanelContentPaddingSx() {
  return {
    pl: 3,
    pt: 3,
    pb: 3,
    pr: 2,
    mr: 0.75,
    [INSIGHT_PANEL_NARROW]: { pl: 2.25, pt: 2.25, pb: 2.25, pr: 1.5, mr: 0.5 },
    [INSIGHT_PANEL_COMPACT]: { pl: 1.75, pt: 2, pb: 2, pr: 1.25, mr: 0.25 },
  };
}

/** Linha checkbox + rótulo + barra + % — barra vai para baixo em painéis estreitos. */
export const insightMetricRowLayoutSx = {
  flexWrap: "wrap",
  alignItems: "center",
  rowGap: 1,
  columnGap: 1.25,
  "& .insight-metric-label": {
    flex: "1 1 8rem",
    minWidth: 0,
    [INSIGHT_PANEL_NARROW]: { whiteSpace: "normal", wordBreak: "break-word" },
    [INSIGHT_PANEL_WIDE]: { whiteSpace: "nowrap" },
  },
  "& .insight-metric-bar-wrap": {
    flex: "1 1 100%",
    order: 3,
    minWidth: 0,
    [INSIGHT_PANEL_WIDE]: {
      flex: "0 0 72px",
      order: 0,
      width: 72,
    },
  },
  "& .insight-metric-pct": {
    flexShrink: 0,
    marginLeft: "auto",
    [INSIGHT_PANEL_WIDE]: { marginLeft: 0 },
  },
  "& .insight-metric-arrow": {
    [INSIGHT_PANEL_COMPACT]: { display: "none" },
  },
} as const;

export function insightPanelScrollSx(accentColor: string) {
  const thumb = alpha(accentColor, 0.38);
  const thumbHover = alpha(accentColor, 0.58);
  return {
    overflowY: "auto",
    overflowX: "hidden",
    overscrollBehaviorY: "auto",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "thin",
    scrollbarColor: `${thumb} transparent`,
    "&::-webkit-scrollbar": {
      width: 9,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
      marginBlock: 10,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: thumb,
      borderRadius: 8,
      border: "2px solid transparent",
      backgroundClip: "padding-box",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: thumbHover,
    },
  };
}

const ANALYSIS_COLOR_FALLBACKS: Record<AnalysisCardId, string> = {
  tratamentos: "#2e7d32",
  protocolos: "#ed6c02",
  condutas: "#9c27b0",
  problemas: "#FF5722",
  planos: "#1976d2",
  diagnosticos: "#0288d1",
  service: "#00897B",
  alergias: "#d32f2f",
};

export function getAnalysisAccentColor(theme: Theme, id: AnalysisCardId): string {
  const palette = theme.palette;
  switch (id) {
    case "tratamentos":
      return palette.success.main;
    case "protocolos":
      return palette.warning.main;
    case "condutas":
      return palette.secondary.main;
    case "problemas":
      return palette.deepOrange?.main ?? ANALYSIS_COLOR_FALLBACKS.problemas;
    case "planos":
      return palette.primary.main;
    case "diagnosticos":
      return palette.info.main;
    case "service":
      return ANALYSIS_COLOR_FALLBACKS.service;
    case "alergias":
      return palette.error.main;
    default:
      return ANALYSIS_COLOR_FALLBACKS[id];
  }
}

export function analysisBg(color: string, opacity = BG_ALPHA): string {
  return alpha(color, opacity);
}

export function analysisBorder(color: string, opacity = BORDER_ALPHA): string {
  return alpha(color, opacity);
}

export const ANALYSIS_COLORS = ANALYSIS_COLOR_FALLBACKS;
